/**
 * creditGate — the free grant is money, and money is what opens the door.
 *
 * CREDIT-GATE-HOTFIX-1. A brand new account with the full grant —
 * Mockaccounttest Business, `balance_cents` 10000, no plan — was refused by the
 * panel. METER-TRUTH-2 put a pre-call gate on `assistant-plan` and wired it to
 * `checkAiAllowed`, which asks two questions and neither is about money:
 *
 *   1. is the plan active? `subscription_status` is null, so no.
 *   2. is there FREE TIER left for this event type? `claude_request_simple` has
 *      `free_tier_monthly_count = null` — it has no free tier at all — so
 *      `fetchFreeAllowance` returns `remaining: null`, the loop `continue`s,
 *      and the function returns false.
 *
 * **THE WALLET IS NEVER READ.** A hundred dollars of real money was invisible
 * to the decision. Measured 2026-09-05: **ten accounts holding 60,400 cents
 * between them** were refused this way.
 *
 * PURE, so mutation testing can run on it. No client and no fetch.
 */

/** What an account can actually spend, in cents. */
// THE .ts SUFFIX IS LOAD-BEARING. Deno reaches this file through the edge
// bundler, which resolves no extensionless specifier: without it
// `generate-artifact` fails to bundle with "Module not found". The repository
// writes cross-boundary imports this way already - see `requestShape.ts` in
// `buildRoute.ts`.
import { FIRST_BUILD_EVENT, isAbsorbed } from './firstBuildPrice.ts'

/**
 * AN ACTION PRICED AT ZERO NEEDS NO MONEY TO START.
 *
 * FREE-FIRST-BUILD-1. Without this the free first build is refused at the
 * door: a brand new account has no plan, no money, and the monthly free tier
 * is deliberately NOT the first build's gate — its gate is a person row. So
 * every one of `allowedToStart`'s three ways in says no, to the one thing the
 * owner ruled must be free.
 *
 * It is a whitelist of events this product prices at zero, not a check of a
 * number, because the number lives in `cost_dictionary` and a gate that
 * trusted a passed-in zero would open on any caller that said "zero".
 */
export function costsNothing(eventType: string | null | undefined): boolean {
  return eventType === FIRST_BUILD_EVENT || isAbsorbed(String(eventType))
}

export interface Spendable {
  /** Money the merchant put in, or was granted. */
  balanceCents: number
  /** Credit that came with a plan. */
  planCreditCents: number
  /** Credit ringfenced for builds. */
  buildCreditCents: number
}

/**
 * THE SMALLEST CHARGE WORTH GATING ON.
 *
 * A call cannot cost less than a whole sub-cent tick, and the meter refuses per
 * call anyway if the money runs out mid-flight. So the gate asks only whether
 * there is ANY money here, not whether there is enough for a call whose cost
 * nobody knows until it has been made. One cent is the floor: below that the
 * account genuinely cannot pay for anything.
 */
export const MINIMUM_CENTS = 1

/**
 * EVERYTHING SPENDABLE, ADDED UP.
 *
 * All three are money to the merchant and all three are money to the meter.
 * A gate that reads one of them and calls it the balance is the bug this
 * module exists for.
 */
export function spendableCents(w: Spendable): number {
  const parts = [w.balanceCents, w.planCreditCents, w.buildCreditCents]
  let total = 0
  for (const p of parts) {
    if (Number.isFinite(p)) total += p
  }
  return total
}

/**
 * WHETHER MONEY ALONE OPENS THE DOOR.
 *
 * ON EVERY PLAN INCLUDING NONE. There is no plan in this function on purpose:
 * a merchant who has paid for credits has paid for credits, and making them
 * also hold a subscription is charging twice for one thing.
 */
export function moneyCovers(w: Spendable, minimumCents: number = MINIMUM_CENTS): boolean {
  return spendableCents(w) >= minimumCents
}

/**
 * WHAT THE MERCHANT READS WHEN IT IS GENUINELY OUT.
 *
 * NEVER A CODE WORD. The panel was showing "We could not reach Ghost, so
 * nothing has been asked. no_credits" — two sentences that contradict each
 * other, followed by an identifier meant for a log. A merchant cannot act on
 * that; they can act on this.
 */
export const NO_CREDITS_SENTENCE =
  'Ghost cannot start a build right now: this account has no credits left. ' +
  'Add credits or upgrade to continue.'

/**
 * The whole decision, for a caller that already knows the plan and free tier
 * answers. `true` from either of those still wins — this only adds money as a
 * third way in, it takes nothing away.
 */
export function allowedToStart(opts: {
  planActive: boolean
  freeTierRemains: boolean
  wallet: Spendable | null
  minimumCents?: number
  /**
   * The action costs nothing, from `costsNothing`. Default false, so every
   * caller that does not pass it behaves exactly as it did.
   */
  costsNothing?: boolean
}): boolean {
  // FIRST, because it is the only one that does not ask about money at all.
  if (opts.costsNothing === true) return true
  if (opts.planActive) return true
  if (opts.freeTierRemains) return true
  // A wallet we could not read is not a wallet with nothing in it, but it is
  // also not permission to spend. The caller decides how to surface that; the
  // gate itself will not invent money.
  if (opts.wallet === null) return false
  return moneyCovers(opts.wallet, opts.minimumCents ?? MINIMUM_CENTS)
}

/**
 * A REFUSAL IS NOT AN OUTAGE, AND A CODE WORD IS NOT A SENTENCE.
 *
 * `assistant-plan` answers a gated request with `{ok: false, error:
 * 'no_credits'}`. The panel's fallback branch put that straight into the
 * `unreachable` state, whose renderer prefixes "We could not reach Ghost, so
 * nothing has been asked." — so the merchant read a contradiction with an
 * identifier on the end: **"We could not reach Ghost, so nothing has been
 * asked. no_credits"**. Ghost was reached. It answered. The answer was no.
 *
 * Returns the sentence for a refusal this module knows, and null for anything
 * else — an unknown code really is an outage and must keep going to the
 * `unreachable` branch rather than being dressed up as a tidy refusal.
 */
/**
 * LAST-HOP-1 — THE DOOR'S TWO REFUSALS, IN GHOST'S OWN WORDS.
 *
 * These live HERE rather than beside the door because two places said them:
 * the door puts a sentence on its error, and the panel maps a code to one. Two
 * copies of the same words drift, and the drift is invisible because both
 * sides go on working. `aiModelRegistry.ts` imports them from this file, the
 * way `planEntitlement.ts` and `assistant-plan` already import from it.
 *
 * NO CODE WORD, AND NO DASH. A merchant never reads `tier`, an event type, an
 * HTTP status, or a word that only means something to us.
 */

/** We asked what this costs, and there is no price set for it. */
export const TIER_CLOSED_SENTENCE =
  'I am not able to build right now. Nothing has been spent, and nothing on ' +
  'your account has changed.'

/** WE COULD NOT ASK. A different fact, and it must not look like the one above. */
export const TIER_UNREADABLE_SENTENCE =
  'I could not check whether I am able to build right now, so I have not ' +
  'started. Nothing has changed.'

/**
 * THE MAP, and the only place a code becomes a sentence.
 *
 * A code this module does not know still returns null ON PURPOSE, so the panel
 * keeps sending it to the `unreachable` branch: an unknown code really is an
 * outage, and dressing one up as a tidy refusal would hide it.
 */
/**
 * OUTAGE-CARD-REFUSAL-1 — what the meter said, in Ghost's own words.
 *
 * The meter REFUSES for reasons a merchant can act on, and each of those is a
 * different fact. Every one of these ends by saying nothing was taken, because
 * that is the merchant's first question and in every case here it is true.
 */
export const CARD_REQUIRED_SENTENCE =
  'I have not built that. There is no payment method on this account, so the ' +
  'charge could not be placed. Nothing has been taken from you.'

export const SPEND_LIMIT_SENTENCE =
  'I have not built that. It would go past the spending limit set on this ' +
  'account. Nothing has been taken from you.'

export const DAILY_CAP_SENTENCE =
  'I have not built that. This account has reached what it is allowed to run ' +
  'today. Nothing has been taken from you.'

/**
 * THE-CARD-GATE-READS-THE-MONEY-1 — WHEN A CODE ARRIVES WITH NO SENTENCE.
 *
 * That is OUR fault, not the merchant's, and it says so rather than showing
 * them the code. The old fallback put the raw code into the `unreachable`
 * renderer, which prefixes "We could not reach Ghost, so nothing has been
 * asked" — a code word AND a lie, because Ghost was reached and it answered.
 */
export const UNNAMED_REFUSAL_SENTENCE =
  'I could not do that, and I do not have a clear reason to give you. That is ' +
  'our fault rather than anything you did. Nothing has been taken from you.'

export function refusalSentence(code: string | null | undefined): string | null {
  if (code === 'no_credits') return NO_CREDITS_SENTENCE
  if (code === 'tier_closed') return TIER_CLOSED_SENTENCE
  if (code === 'tier_unreadable') return TIER_UNREADABLE_SENTENCE
  if (code === 'card_required') return CARD_REQUIRED_SENTENCE
  if (code === 'spend_limit') return SPEND_LIMIT_SENTENCE
  if (code === 'daily_cap') return DAILY_CAP_SENTENCE
  return null
}
